import type { RemotePresenceState } from '../types/presentationRemote';

const DEVICE_LS = 'cp-pres-remote-device-v1';
const SESSION_SS = 'cp-pres-remote-session-v1';
const NAMES_LS_PREFIX = 'cp-pres-remote-names-v1:';
export const REMOTE_AUTO_NAME_PREFIX = '메인 프레젠테이션';
export const REMOTE_NAME_MAX = 30;

export function randomId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    /* ignore */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** 기기 고정 id (localStorage) */
export function getRemoteDeviceId(): string {
  try {
    const cur = window.localStorage.getItem(DEVICE_LS);
    if (cur && cur.length >= 8) return cur;
    const next = randomId();
    window.localStorage.setItem(DEVICE_LS, next);
    return next;
  } catch {
    return `volatile-${randomId()}`;
  }
}

const CLAIM_BC = 'cp-pres-remote-claim-v1';
const CLAIM_WAIT_MS = 250;
let sessionIdPromise: Promise<string> | null = null;
let claimChannel: BroadcastChannel | null = null;

function writeSessionId(id: string) {
  try {
    window.sessionStorage.setItem(SESSION_SS, id);
  } catch {
    /* ignore */
  }
}

/** 이 탭이 쓰는 id를 다른 탭이 물어보면 "사용 중"이라고 답한다 (탭 수명 동안 유지) */
function listenClaims(id: string) {
  if (typeof BroadcastChannel === 'undefined') return;
  try {
    claimChannel = new BroadcastChannel(CLAIM_BC);
    claimChannel.onmessage = (ev: MessageEvent) => {
      const m = ev.data as { kind?: string; id?: string } | null;
      if (m?.kind === 'who' && m.id === id) claimChannel?.postMessage({ kind: 'taken', id });
    };
  } catch {
    claimChannel = null;
  }
}

/** sessionStorage 후보 id를 다른 살아 있는 탭(복제 탭)이 쓰고 있는지 확인 */
function isIdTakenByOtherTab(id: string): Promise<boolean> {
  if (typeof BroadcastChannel === 'undefined') return Promise.resolve(false);
  return new Promise((resolve) => {
    let bc: BroadcastChannel | null = null;
    let done = false;
    const finish = (v: boolean) => {
      if (done) return;
      done = true;
      try {
        bc?.close();
      } catch {
        /* ignore */
      }
      resolve(v);
    };
    try {
      bc = new BroadcastChannel(CLAIM_BC);
      bc.onmessage = (ev: MessageEvent) => {
        const m = ev.data as { kind?: string; id?: string } | null;
        if (m?.kind === 'taken' && m.id === id) finish(true);
      };
      bc.postMessage({ kind: 'who', id });
      window.setTimeout(() => finish(false), CLAIM_WAIT_MS);
    } catch {
      finish(false);
    }
  });
}

/**
 * 탭 세션 id — 탭 수명 동안 1개 (모듈 단위 memo → StrictMode 이중 effect에도 동일).
 * 새로고침: sessionStorage 값을 재사용 → presence가 교체됨(중복 아님).
 * 탭 복제: sessionStorage가 복사되지만 원본 탭이 BroadcastChannel로 "사용 중" 응답 → 새 id 발급.
 */
export function acquireRemoteSessionId(): Promise<string> {
  if (sessionIdPromise) return sessionIdPromise;
  sessionIdPromise = (async () => {
    let candidate: string | null = null;
    try {
      candidate = window.sessionStorage.getItem(SESSION_SS);
    } catch {
      candidate = null;
    }
    if (candidate && (await isIdTakenByOtherTab(candidate))) candidate = null;
    const id = candidate || randomId();
    writeSessionId(id);
    listenClaims(id);
    return id;
  })();
  return sessionIdPromise;
}

type NameMap = Record<string, string>;

function namesKey(userId: string, deviceId: string) {
  return `${NAMES_LS_PREFIX}${userId}:${deviceId}`;
}

function readNames(userId: string, deviceId: string): NameMap {
  try {
    const raw = window.localStorage.getItem(namesKey(userId, deviceId));
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? (parsed as NameMap) : {};
  } catch {
    return {};
  }
}

export function autoPresentationName(n: number): string {
  return `${REMOTE_AUTO_NAME_PREFIX} ${n}`;
}

/** 계정+기기+자동번호 기준 사용자 지정 이름 */
export function getCustomPresentationName(userId: string, deviceId: string, autoNumber: number): string | null {
  const v = readNames(userId, deviceId)[String(autoNumber)];
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

export function sanitizeRemoteName(name: string): string {
  return String(name ?? '').replace(/\s+/g, ' ').trim().slice(0, REMOTE_NAME_MAX);
}

/** 빈 문자열이면 사용자 지정 이름 삭제(자동 이름 복귀) */
export function setCustomPresentationName(userId: string, deviceId: string, autoNumber: number, name: string): void {
  try {
    const map = readNames(userId, deviceId);
    const clean = sanitizeRemoteName(name);
    if (clean) map[String(autoNumber)] = clean;
    else delete map[String(autoNumber)];
    window.localStorage.setItem(namesKey(userId, deviceId), JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

export function resolvePresentationName(userId: string, deviceId: string, autoNumber: number): string {
  return getCustomPresentationName(userId, deviceId, autoNumber) ?? autoPresentationName(autoNumber);
}

/** 계정의 다른 온라인 프레젠이 쓰지 않는 가장 작은 번호 */
export function lowestFreeAutoNumber(others: ReadonlyArray<RemotePresenceState>, selfSessionId: string): number {
  const used = new Set<number>();
  for (const p of others) {
    if (p.role !== 'presentation' || p.sessionId === selfSessionId) continue;
    if (typeof p.autoNumber === 'number' && p.autoNumber > 0) used.add(p.autoNumber);
  }
  let n = 1;
  while (used.has(n)) n += 1;
  return n;
}

const ALLOW_REMOTE_LS_PREFIX = 'cp-pres-remote-allow-v1:';
/** 원격 제어를 켜 둔 뒤 자동으로 끄는 시간 */
export const REMOTE_ALLOW_MS = 60 * 60 * 1000;

const allowRemoteKey = (userId: string, deviceId: string) =>
  `${ALLOW_REMOTE_LS_PREFIX}${userId}:${deviceId}`;

/** 켜 둔 시각의 만료 시각. 없거나 지났거나 예전 영구값('1')이면 null */
export function readAllowRemoteUntil(userId: string, deviceId: string): number | null {
  try {
    const key = allowRemoteKey(userId, deviceId);
    const raw = window.localStorage.getItem(key);
    const until = Number(raw);
    if (!raw || raw === '0' || raw === '1' || !Number.isFinite(until) || until <= Date.now()) {
      if (raw) window.localStorage.removeItem(key);
      return null;
    }
    return until;
  } catch {
    return null;
  }
}

/** 프레젠: '원격 제어' (계정+기기별, 기본 OFF, 켜면 1시간 뒤 만료) */
export function readAllowRemoteControl(userId: string, deviceId: string): boolean {
  return readAllowRemoteUntil(userId, deviceId) != null;
}

export function writeAllowRemoteControl(userId: string, deviceId: string, on: boolean): number | null {
  try {
    const key = allowRemoteKey(userId, deviceId);
    if (!on) {
      window.localStorage.removeItem(key);
      return null;
    }
    const until = Date.now() + REMOTE_ALLOW_MS;
    window.localStorage.setItem(key, String(until));
    return until;
  } catch {
    return null;
  }
}
